import { useId, useRef, useState, type ReactNode } from 'react'
import { FileTextIcon, LockIcon, UploadIcon, XIcon } from 'lucide-react'

import {
  appliesTo,
  checkDetails,
  isBlank,
  standardLabel,
  STANDARD_FIELDS,
  type ProfileField,
  type RegistrationForm,
  type StandardFieldKey,
} from '@ems/contracts/profile-fields'
import { Button } from '@ems/ui/components/button'
import { Checkbox } from '@ems/ui/components/checkbox'
import { Input } from '@ems/ui/components/input'
import { Textarea } from '@ems/ui/components/textarea'
import { toast } from '@ems/ui/components/sonner'
import { CountrySelect } from '@ems/ui/patterns/country-select'
import { Field } from '@ems/ui/patterns/field'
import { PhoneInput } from '@ems/ui/patterns/phone-input'

import { OptionSelect } from '#admin/components/option-select.tsx'
import { fileNameOf, UPLOAD_LIMITS, UploadRefused, uploadFile } from '#admin/lib/upload.ts'

/**
 * One participant's details, rendered from the edition's registration form.
 *
 * **The one renderer.** The Settings preview, the New registration page and the Edit participant
 * sheet all draw this, so what an operator previews is exactly what a registrant is asked — there
 * is no second form to drift from the first.
 *
 * Three groups, so a long form stays scannable: **Contact**, **Badge and work**, and **More
 * details** (dietary requirements and the edition's own questions, in its order). A group with
 * nothing shown is not drawn. Only the questions the chosen ticket type asks appear.
 */

/** A value while it is being typed: numbers stay text until they are sent. */
export type DraftValue = string | string[] | boolean | undefined

export type ParticipantDraft = {
  fullName: string
  email: string
  standard: Record<StandardFieldKey, string>
  answers: Record<string, DraftValue>
}

export function emptyDraft(): ParticipantDraft {
  return {
    fullName: '',
    email: '',
    standard: {
      phone: '',
      nationality: '',
      badgeName: '',
      jobTitle: '',
      companyName: '',
      dietaryRequirements: '',
    },
    answers: {},
  }
}

/** What a stored answer looks like in the form: a number as its digits, the rest as it is. */
export function draftValue(value: unknown): DraftValue {
  if (typeof value === 'number') return String(value)
  if (typeof value === 'string' || typeof value === 'boolean') return value
  if (Array.isArray(value)) return value.filter((item): item is string => typeof item === 'string')
  return undefined
}

/**
 * The answers to send: only the questions this ticket asks, blanks left out, numbers as numbers.
 * A number that does not parse is sent as the text, so the check names it rather than dropping it.
 */
export function answersOf(
  form: RegistrationForm,
  ticketTypeId: string,
  answers: Record<string, DraftValue>,
): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  for (const field of form.fields) {
    if (field.archived || !appliesTo(field, ticketTypeId)) continue
    const value = answers[field.key]
    if (isBlank(value)) continue
    if (field.type === 'number' && typeof value === 'string') {
      const number = Number(value.trim())
      out[field.key] = Number.isFinite(number) ? number : value
    } else {
      out[field.key] = value
    }
  }
  return out
}

/** Answers the draft holds for questions this ticket does not ask — shown before they are dropped. */
export function droppedAnswers(
  form: RegistrationForm,
  ticketTypeId: string,
  answers: Record<string, DraftValue>,
): string[] {
  return form.fields
    .filter((field) => !field.archived && !appliesTo(field, ticketTypeId))
    .filter((field) => !isBlank(answers[field.key]))
    .map((field) => field.label)
}

/**
 * The same checks the API makes, run here so the message under a field is the server's own —
 * plus the two the form always asks, name and email. `requireComplete` adds the required ones.
 */
export function draftErrors(
  form: RegistrationForm,
  ticketTypeId: string,
  draft: ParticipantDraft,
  requireComplete: boolean,
): Record<string, string> {
  const errors: Record<string, string> = {}
  if (requireComplete && draft.fullName.trim() === '') errors['fullName'] = 'Full name is required.'
  if (requireComplete && draft.email.trim() === '') errors['email'] = 'Email is required.'
  else if (draft.email.trim() !== '' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(draft.email.trim())) {
    errors['email'] = 'Enter an email address, like name@example.com.'
  }
  if (ticketTypeId === '') return errors

  const result = checkDetails(
    form,
    {
      ticketTypeId,
      standard: draft.standard,
      answers: answersOf(form, ticketTypeId, draft.answers),
    },
    { requireComplete },
  )
  for (const issue of result.issues) errors[issue.field] ??= issue.message
  return errors
}

type Props = {
  form: RegistrationForm
  ticketTypeId: string
  draft: ParticipantDraft
  onChange: (draft: ParticipantDraft) => void
  errors?: Record<string, string>
  /** Name and email are fixed on a correction that must not move somebody's tickets. */
  identityLocked?: boolean
  /** Uploading needs `identity.write`; without it a file question explains rather than fails. */
  canUpload?: boolean
  /** Sensitive answers still masked, by key — shown read-only until revealed. */
  masked?: ReadonlySet<string>
}

const NO_ERRORS: Record<string, string> = {}
const NONE_MASKED: ReadonlySet<string> = new Set()

export function ParticipantFields({
  form,
  ticketTypeId,
  draft,
  onChange,
  errors = NO_ERRORS,
  identityLocked = false,
  canUpload = true,
  masked = NONE_MASKED,
}: Props) {
  const setStandard = (key: StandardFieldKey, value: string) =>
    onChange({ ...draft, standard: { ...draft.standard, [key]: value } })
  const setAnswer = (key: string, value: DraftValue) =>
    onChange({ ...draft, answers: { ...draft.answers, [key]: value } })

  const shown = (key: StandardFieldKey) => form.standard[key].shown
  const custom = form.fields.filter((f) => !f.archived && appliesTo(f, ticketTypeId))

  const standardField = (key: StandardFieldKey, control: (props: ControlProps) => ReactNode) =>
    shown(key) ? (
      <Field
        key={key}
        label={standardLabel(key, form.standard)}
        required={form.standard[key].required}
        error={errors[key]}
        {...(key === 'dietaryRequirements' ? { className: '@lg:col-span-2' } : {})}
      >
        {control}
      </Field>
    ) : null

  const text = (key: StandardFieldKey, max: number, autoComplete: string) =>
    standardField(key, (props) => (
      <Input
        {...props}
        value={draft.standard[key]}
        maxLength={max}
        autoComplete={autoComplete}
        onChange={(event) => setStandard(key, event.target.value)}
      />
    ))

  const contact = [
    <Field key="fullName" label="Full name" required error={errors['fullName']}>
      {(props) => (
        <Input
          {...props}
          value={draft.fullName}
          maxLength={200}
          autoComplete="off"
          readOnly={identityLocked}
          onChange={(event) => onChange({ ...draft, fullName: event.target.value })}
        />
      )}
    </Field>,
    <Field key="email" label="Email" required error={errors['email']}>
      {(props) => (
        <Input
          {...props}
          type="email"
          value={draft.email}
          maxLength={320}
          autoComplete="off"
          spellCheck={false}
          onChange={(event) => onChange({ ...draft, email: event.target.value })}
        />
      )}
    </Field>,
    standardField('phone', (props) => (
      <PhoneInput
        {...props}
        value={draft.standard.phone}
        defaultCountry={form.defaultCountry}
        onChange={(value) => setStandard('phone', value)}
      />
    )),
  ].filter(Boolean)

  const work = [
    text('badgeName', 120, 'off'),
    text('jobTitle', 200, 'organization-title'),
    text('companyName', 200, 'organization'),
    standardField('nationality', (props) => (
      <CountrySelect
        {...props}
        value={draft.standard.nationality}
        onValueChange={(value) => setStandard('nationality', value)}
      />
    )),
  ].filter(Boolean)

  const more = [
    standardField('dietaryRequirements', (props) => (
      <Textarea
        {...props}
        rows={2}
        maxLength={1000}
        value={draft.standard.dietaryRequirements}
        onChange={(event) => setStandard('dietaryRequirements', event.target.value)}
      />
    )),
    ...custom.map((field) => (
      <AnswerField
        key={field.key}
        field={field}
        value={draft.answers[field.key]}
        onChange={(value) => setAnswer(field.key, value)}
        error={errors[`answers.${field.key}`]}
        defaultCountry={form.defaultCountry}
        canUpload={canUpload}
        masked={masked.has(field.key)}
      />
    )),
  ].filter(Boolean)

  return (
    <div className="flex flex-col gap-6">
      <Group title="Contact">{contact}</Group>
      {work.length > 0 ? <Group title="Badge and work">{work}</Group> : null}
      {more.length > 0 ? <Group title="More details">{more}</Group> : null}
    </div>
  )
}

type ControlProps = {
  id: string
  'aria-describedby': string | undefined
  'aria-invalid': boolean
  required: boolean
}

function Group({ title, children }: { title: string; children: ReactNode }) {
  return (
    <fieldset className="flex flex-col gap-3">
      <legend className="text-muted-foreground mb-3 text-xs font-medium tracking-wide uppercase">
        {title}
      </legend>
      <div className="@container">
        <div className="grid gap-4 @lg:grid-cols-2">{children}</div>
      </div>
    </fieldset>
  )
}

const WIDE: ReadonlySet<ProfileField['type']> = new Set(['long_text', 'multi_select', 'file'])

function AnswerField({
  field,
  value,
  onChange,
  error,
  defaultCountry,
  canUpload,
  masked,
}: {
  field: ProfileField
  value: DraftValue
  onChange: (value: DraftValue) => void
  error: string | undefined
  defaultCountry: string
  canUpload: boolean
  masked: boolean
}) {
  const help = (
    <>
      {field.helpText}
      {field.sensitive ? (
        <span className="mt-0.5 flex items-center gap-1">
          <LockIcon aria-hidden="true" className="size-3" />
          Only registration managers can see this.
        </span>
      ) : null}
    </>
  )
  const hasHelp = field.helpText !== null || field.sensitive
  const wide = WIDE.has(field.type) ? '@lg:col-span-2' : undefined

  if (field.type === 'multi_select') {
    return (
      <ChoiceGroup
        field={field}
        value={value}
        onChange={onChange}
        error={error}
        help={hasHelp ? help : undefined}
      />
    )
  }

  return (
    <Field
      label={field.label}
      required={field.required}
      error={error}
      help={hasHelp ? help : undefined}
      {...(wide === undefined ? {} : { className: wide })}
    >
      {(props) => {
        if (masked) {
          // Still masked: the value is not in the page, so there is nothing to edit yet.
          return <Input {...props} value={typeof value === 'string' ? value : ''} readOnly />
        }
        const text = typeof value === 'string' ? value : ''
        switch (field.type) {
          case 'long_text':
            return (
              <Textarea
                {...props}
                rows={3}
                maxLength={4000}
                value={text}
                onChange={(e) => onChange(e.target.value)}
              />
            )
          case 'select':
            return (
              <div className="flex items-center gap-2">
                <OptionSelect
                  {...props}
                  value={text}
                  placeholder="Choose one…"
                  onValueChange={onChange}
                  options={field.options}
                />
                {!field.required && text !== '' ? (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    aria-label={`Clear ${field.label}`}
                    onClick={() => onChange(undefined)}
                  >
                    <XIcon />
                  </Button>
                ) : null}
              </div>
            )
          case 'yes_no':
            return (
              <OptionSelect
                {...props}
                value={value === true ? 'yes' : value === false ? 'no' : ''}
                placeholder="Choose yes or no…"
                onValueChange={(choice) => onChange(choice === 'yes')}
                options={[
                  { value: 'yes', label: 'Yes' },
                  { value: 'no', label: 'No' },
                ]}
              />
            )
          case 'date':
            return (
              <Input
                {...props}
                type="date"
                value={text}
                onChange={(e) => onChange(e.target.value)}
              />
            )
          case 'number':
            return (
              <Input
                {...props}
                inputMode="decimal"
                value={text}
                onChange={(e) => onChange(e.target.value)}
              />
            )
          case 'email':
            return (
              <Input
                {...props}
                type="email"
                spellCheck={false}
                maxLength={320}
                value={text}
                onChange={(e) => onChange(e.target.value)}
              />
            )
          case 'country':
            return <CountrySelect {...props} value={text} onValueChange={onChange} />
          case 'phone':
            return (
              <PhoneInput
                {...props}
                value={text}
                defaultCountry={defaultCountry}
                onChange={onChange}
              />
            )
          case 'file':
            return (
              <FileAnswer
                {...props}
                value={text}
                onChange={onChange}
                canUpload={canUpload}
                label={field.label}
              />
            )
          default:
            return (
              <Input
                {...props}
                maxLength={500}
                value={text}
                onChange={(e) => onChange(e.target.value)}
              />
            )
        }
      }}
    </Field>
  )
}

/** Several choices: a real `fieldset` with its `legend`, so the question is read with each box. */
function ChoiceGroup({
  field,
  value,
  onChange,
  error,
  help,
}: {
  field: ProfileField
  value: DraftValue
  onChange: (value: DraftValue) => void
  error: string | undefined
  help: ReactNode
}) {
  const id = useId()
  const chosen = Array.isArray(value) ? value : []
  return (
    <fieldset
      className="flex flex-col gap-2 @lg:col-span-2"
      aria-describedby={
        [help === undefined ? null : `${id}-help`, error === undefined ? null : `${id}-error`]
          .filter(Boolean)
          .join(' ') || undefined
      }
      aria-invalid={error !== undefined}
    >
      <legend className="mb-2 flex items-center gap-1 text-sm leading-none font-medium">
        {field.label}
        {field.required ? (
          <span className="text-muted-foreground text-xs font-normal">(required)</span>
        ) : null}
      </legend>
      <div className="grid gap-2 sm:grid-cols-2">
        {field.options.map((option) => (
          <label key={option.value} className="flex min-h-9 items-center gap-2 text-sm">
            <Checkbox
              checked={chosen.includes(option.value)}
              onCheckedChange={(checked) =>
                onChange(
                  checked === true
                    ? [...chosen, option.value]
                    : chosen.filter((item) => item !== option.value),
                )
              }
            />
            {option.label}
          </label>
        ))}
      </div>
      {help === undefined ? null : (
        <p id={`${id}-help`} className="text-muted-foreground text-xs">
          {help}
        </p>
      )}
      {error === undefined ? null : (
        <p id={`${id}-error`} role="alert" className="text-destructive text-xs font-medium">
          {error}
        </p>
      )}
    </fieldset>
  )
}

/**
 * A file question: pick, upload straight to storage, keep the key. The file name is what shows;
 * Replace and Remove sit beside it. Without upload permission it says so instead of failing.
 */
function FileAnswer({
  value,
  onChange,
  canUpload,
  label,
  id,
  'aria-describedby': describedBy,
}: ControlProps & {
  value: string
  onChange: (value: DraftValue) => void
  canUpload: boolean
  label: string
}) {
  const input = useRef<HTMLInputElement>(null)
  const [uploading, setUploading] = useState(false)

  const pick = async (file: File | undefined) => {
    if (file === undefined) return
    setUploading(true)
    try {
      onChange(await uploadFile('profile-document', file))
    } catch (error) {
      toast.error('The file was not uploaded', {
        description: error instanceof UploadRefused ? error.message : 'Try again in a moment.',
      })
    } finally {
      setUploading(false)
      if (input.current !== null) input.current.value = ''
    }
  }

  if (!canUpload && value === '') {
    return (
      <p id={id} aria-describedby={describedBy} className="text-muted-foreground text-sm">
        Your role cannot upload files. Ask an administrator to add it.
      </p>
    )
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      <input
        ref={input}
        type="file"
        className="sr-only"
        tabIndex={-1}
        accept={UPLOAD_LIMITS['profile-document'].accept}
        aria-label={`Choose a file for ${label}`}
        onChange={(event) => void pick(event.target.files?.[0])}
      />
      {value === '' ? null : (
        <span className="bg-muted flex min-h-9 items-center gap-2 rounded-md px-3 text-sm">
          <FileTextIcon aria-hidden="true" className="size-4" />
          {fileNameOf(value)}
        </span>
      )}
      {canUpload ? (
        <Button
          id={id}
          aria-describedby={describedBy}
          type="button"
          variant="outline"
          disabled={uploading}
          onClick={() => input.current?.click()}
        >
          <UploadIcon />
          {uploading ? 'Uploading…' : value === '' ? 'Upload file' : 'Replace'}
        </Button>
      ) : null}
      {value !== '' && canUpload ? (
        <Button type="button" variant="ghost" onClick={() => onChange(undefined)}>
          Remove
        </Button>
      ) : null}
    </div>
  )
}

/** The standard fields' order, for anything that lists a participant's details. */
export const STANDARD_ORDER = STANDARD_FIELDS.map((field) => field.key)
